import React from "react";

/**
 * Top-level error boundary. If any rendered React subtree throws (or a lazy
 * chunk fails to load — e.g. ChunkLoadError after a deploy), this catches
 * the error and renders a recovery UI instead of an empty white page.
 *
 * Two recovery actions:
 *   - "Reload page" — most chunk-load errors resolve after a hard reload
 *     because index.html now has the new chunk hashes.
 *   - "Go home" — bail to the homepage if the current route is broken.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (typeof window !== "undefined" && window.console) {
      console.error("[ErrorBoundary] caught:", error, info);
    }
    // Auto-reload on chunk load errors — these usually self-heal after a deploy.
    const isChunkError =
      error &&
      (error.name === "ChunkLoadError" ||
        /Loading chunk \d+ failed/i.test(error.message || "") ||
        /Loading CSS chunk \d+ failed/i.test(error.message || ""));
    if (isChunkError && typeof window !== "undefined") {
      const KEY = "__seointent_chunk_reload_attempted";
      if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, "1");
        window.location.reload();
      }
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily:
            "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
          background: "#fff",
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          <img
            src="/logo-red.png"
            alt="SEOintent"
            width="160"
            style={{ marginBottom: 24, opacity: 0.9 }}
          />
          <h1 style={{ fontSize: 24, marginBottom: 12, color: "#18181b" }}>
            Something went wrong
          </h1>
          <p style={{ color: "#52525b", marginBottom: 24, lineHeight: 1.5 }}>
            We hit a snag rendering this page. A reload usually fixes it.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: "#FF2F2F",
                color: "#fff",
                border: 0,
                padding: "12px 24px",
                borderRadius: 10,
                fontWeight: 600,
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Reload page
            </button>
            <a
              href="/"
              style={{
                color: "#18181b",
                background: "#f4f4f5",
                padding: "12px 24px",
                borderRadius: 10,
                fontWeight: 600,
                textDecoration: "none",
                fontSize: 14,
              }}
            >
              Go home
            </a>
          </div>
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre
              style={{
                marginTop: 24,
                padding: 12,
                background: "#fef2f2",
                color: "#991b1b",
                borderRadius: 8,
                fontSize: 11,
                textAlign: "left",
                overflow: "auto",
                maxHeight: 200,
              }}
            >
              {String(this.state.error.stack || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
